import { useCallback } from 'react';

import type {
  DashboardTab,
  MarketAppWithTabs,
  TileBigNumber,
  WidgetV2
} from '@/features/Dashboard/types';
import { useAppDispatch } from '@/hooks/redux-hooks';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';

import {
  addApp,
  addTab,
  addTabToApp,
  addTile,
  createDashboardDraft,
  deleteTab,
  ensureTabExistsInOtherDashboard,
  markDirty,
  moveAppTabToOtherDashboard,
  moveAppToOtherDashboard,
  moveTabBetweenApps,
  removeApp,
  removeTabFromApp,
  removeTile,
  renameAppTab,
  renameDashboard,
  renameTab,
  reorderApps,
  reorderTabs,
  reorderTabsInsideApp,
  reorderTiles,
  resetDraft,
  resetToEmpty,
  setAllDashboardsData,
  setCurrentTab,
  setInsertPosition,
  updateAppTab,
  removeEmptyApps,
  updateCurrentTabApps,
  updateCurrentTabTiles,
  updateTile
} from '../model/dashboardEdit.slice';
import {
  moveTabToOtherDashboardThunk,
  moveTileToOtherDashboardThunk,
  createUniqueDashboardDraftThunk
} from '../model/dashboardEdit.thunks';

/**
 * Bound action dispatchers for dashboard edit.
 * Provides intent-based API for UI components.
 *
 * All actions dispatch to Redux - no UI state management.
 * All payloads are serializable - no functions, no closures.
 */
export const useDashboardEditActions = () => {
  const dispatch = useAppDispatch();
  const { showErrorSnackbar } = useSnackbarNotifications();

  return {
    // ========================================================================
    // Dashboard
    // ========================================================================

    renameDashboard: useCallback((name: string) => dispatch(renameDashboard({ name })), [dispatch]),

    resetDraft: useCallback(() => dispatch(resetDraft()), [dispatch]),

    resetToEmpty: useCallback(() => dispatch(resetToEmpty()), [dispatch]),

    markDirty: useCallback(() => dispatch(markDirty()), [dispatch]),

    // ========================================================================
    // Tabs
    // ========================================================================

    setCurrentTab: useCallback((tabId: string) => dispatch(setCurrentTab({ tabId })), [dispatch]),

    addTab: useCallback(
      (tabId: string, name: string) => dispatch(addTab({ tabId, name })),
      [dispatch]
    ),

    renameTab: useCallback(
      (tabId: string, name: string) => dispatch(renameTab({ tabId, name })),
      [dispatch]
    ),

    deleteTab: useCallback((tabId: string) => dispatch(deleteTab({ tabId })), [dispatch]),

    reorderTabs: useCallback(
      (fromIndex: number, toIndex: number) => dispatch(reorderTabs({ fromIndex, toIndex })),
      [dispatch]
    ),

    // ========================================================================
    // Apps
    // ========================================================================

    addApp: useCallback(
      (tabId: string, app: MarketAppWithTabs, insertAtIndex?: number) =>
        dispatch(addApp({ tabId, app, insertAtIndex })),
      [dispatch]
    ),

    removeApp: useCallback(
      (tabId: string, appId: string) => dispatch(removeApp({ tabId, appId })),
      [dispatch]
    ),

    renameAppTab: useCallback(
      (tabId: string, appId: string, itemId: string, name: string) =>
        dispatch(renameAppTab({ tabId, appId, itemId, name })),
      [dispatch]
    ),

    updateAppTab: useCallback(
      (tabId: string, appId: string, itemId: string, tab: WidgetV2) =>
        dispatch(updateAppTab({ tabId, appId, itemId, tab })),
      [dispatch]
    ),

    reorderApps: useCallback(
      (tabId: string, fromIndex: number, toIndex: number) =>
        dispatch(reorderApps({ tabId, fromIndex, toIndex })),
      [dispatch]
    ),

    addTabToApp: useCallback(
      (tabId: string, appId: string, tab: WidgetV2, insertAtIndex?: number) =>
        dispatch(addTabToApp({ tabId, appId, tab, insertAtIndex })),
      [dispatch]
    ),

    removeTabFromApp: useCallback(
      (tabId: string, appId: string, itemId: string) =>
        dispatch(removeTabFromApp({ tabId, appId, itemId })),
      [dispatch]
    ),

    moveTabBetweenApps: useCallback(
      (
        ...[tabId, itemId, fromAppId, toAppId, insertAtIndex, keepEmpty]: [
          string,
          string,
          string,
          string,
          number?,
          boolean?
        ]
      ) =>
        dispatch(
          moveTabBetweenApps({ tabId, itemId, fromAppId, toAppId, insertAtIndex, keepEmpty })
        ),
      [dispatch]
    ),

    reorderTabsInsideApp: useCallback(
      (tabId: string, appId: string, fromIndex: number, toIndex: number) =>
        dispatch(reorderTabsInsideApp({ tabId, appId, fromIndex, toIndex })),
      [dispatch]
    ),

    // ========================================================================
    // Tiles
    // ========================================================================

    addTile: useCallback(
      (tabId: string, tile: TileBigNumber, insertAtIndex?: number) =>
        dispatch(addTile({ tabId, tile, insertAtIndex })),
      [dispatch]
    ),

    removeTile: useCallback(
      (tabId: string, tileId: string) => dispatch(removeTile({ tabId, tileId })),
      [dispatch]
    ),

    updateTile: useCallback(
      (tabId: string, tileId: string, tile: TileBigNumber) =>
        dispatch(updateTile({ tabId, tileId, tile })),
      [dispatch]
    ),

    reorderTiles: useCallback(
      (tabId: string, fromIndex: number, toIndex: number) =>
        dispatch(reorderTiles({ tabId, fromIndex, toIndex })),
      [dispatch]
    ),

    // ========================================================================
    // Batch Updates (for DnD operations)
    // ========================================================================

    updateCurrentTabApps: useCallback(
      (apps: MarketAppWithTabs[]) => dispatch(updateCurrentTabApps({ apps })),
      [dispatch]
    ),

    removeEmptyApps: useCallback(() => dispatch(removeEmptyApps()), [dispatch]),

    updateCurrentTabTiles: useCallback(
      (tiles: TileBigNumber[]) => dispatch(updateCurrentTabTiles({ tiles })),
      [dispatch]
    ),

    // ========================================================================
    // UI State
    // ========================================================================

    setInsertPosition: useCallback(
      (position: { index: number | null; afterId: string | null } | null) =>
        dispatch(setInsertPosition(position)),
      [dispatch]
    ),

    // ========================================================================
    // Cross-Dashboard
    // ========================================================================

    createDashboardDraft: useCallback(
      (name: string) => dispatch(createDashboardDraft({ name })),
      [dispatch]
    ),

    createUniqueDashboardDraft: useCallback(
      () => dispatch(createUniqueDashboardDraftThunk()),
      [dispatch]
    ),

    setAllDashboardsData: useCallback(
      (data: Record<string, DashboardTab>) => dispatch(setAllDashboardsData({ data })),
      [dispatch]
    ),

    moveAppToOtherDashboard: useCallback(
      (targetDashboardName: string, targetTabId: string, app: MarketAppWithTabs) =>
        dispatch(moveAppToOtherDashboard({ targetDashboardName, targetTabId, app })),
      [dispatch]
    ),

    moveAppTabToOtherDashboard: useCallback(
      (targetDashboardName: string, targetTabId: string, tab: WidgetV2) =>
        dispatch(moveAppTabToOtherDashboard({ targetDashboardName, targetTabId, tab })),
      [dispatch]
    ),

    moveTileToOtherDashboard: useCallback(
      async (targetDashboardName: string, targetTabId: string, tile: TileBigNumber) => {
        const result = await dispatch(
          moveTileToOtherDashboardThunk({ targetDashboardName, targetTabId, tile })
        );

        // Handle validation errors
        if (moveTileToOtherDashboardThunk.rejected.match(result)) {
          showErrorSnackbar(result.payload as string);
          return false;
        }

        return true;
      },
      [dispatch, showErrorSnackbar]
    ),

    moveTabToOtherDashboard: useCallback(
      async (targetDashboardName: string, tabId: string, tabName: string) => {
        const result = await dispatch(
          moveTabToOtherDashboardThunk({ targetDashboardName, tabId, tabName })
        );

        // Handle validation errors
        if (moveTabToOtherDashboardThunk.rejected.match(result)) {
          showErrorSnackbar(result.payload as string);
          return false;
        }

        return true;
      },
      [dispatch, showErrorSnackbar]
    ),

    ensureTabExistsInOtherDashboard: useCallback(
      (dashboardName: string, tabId: string, tabName: string) =>
        dispatch(ensureTabExistsInOtherDashboard({ dashboardName, tabId, tabName })),
      [dispatch]
    )
  };
};
